import { useEffect, useState } from 'react';

import { listPayrollImports } from '../services/payrollImportsService';

export type PayrollCompetenceTotalsRow = {
  competence: string;
  sortKey: string | null;
  payrollTotal: number;
  earnings: number;
  deductions: number;
  primaryImportId?: string;
};

const computeImportTotals = (importDoc: Awaited<ReturnType<typeof listPayrollImports>>[number]) => {
  const earnings =
    typeof importDoc.totals?.earning?.total === 'number'
      ? importDoc.totals.earning.total
      : importDoc.collaborators.reduce((sum, col) => sum + (col.earningsTotal ?? 0), 0);
  const deductions =
    typeof importDoc.totals?.deduction?.total === 'number'
      ? importDoc.totals.deduction.total
      : importDoc.collaborators.reduce((sum, col) => sum + (col.deductionsTotal ?? 0), 0);
  const payrollTotal = earnings - deductions;
  return { earnings, deductions, payrollTotal };
};

export function usePayrollCompetenceTotals() {
  const [totals, setTotals] = useState<PayrollCompetenceTotalsRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    const fetchTotals = async () => {
      setLoading(true);
      setError(null);
      try {
        const imports = await listPayrollImports();
        if (!active) return;
        const rowsMap = new Map<string, PayrollCompetenceTotalsRow>();
        imports.forEach((item) => {
          const normalized = typeof item.competence === 'string' && item.competence.trim() ? item.competence.trim() : '';
          const label = normalized || 'Sem competência';
          const row =
            rowsMap.get(label) ??
            rowsMap
              .set(label, {
                competence: label,
                sortKey: normalized || null,
                payrollTotal: 0,
                earnings: 0,
                deductions: 0,
                primaryImportId: undefined,
              })
              .get(label)!;

          const importTotals = computeImportTotals(item);
          row.payrollTotal += importTotals.payrollTotal;
          row.earnings += importTotals.earnings;
          row.deductions += importTotals.deductions;
          if (!row.primaryImportId) {
            row.primaryImportId = item.id;
          }
        });

        const sortedRows = Array.from(rowsMap.values()).sort((a, b) => {
          if (a.sortKey === b.sortKey) return 0;
          if (!a.sortKey) return 1;
          if (!b.sortKey) return -1;
          return b.sortKey.localeCompare(a.sortKey);
        });
        setTotals(sortedRows);
      } catch (error) {
        console.error('[usePayrollCompetenceTotals] failed to load payroll imports', error);
        if (active) {
          setTotals([]);
          setError('Não foi possível carregar as importações de folha.');
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    void fetchTotals();
    return () => {
      active = false;
    };
  }, []);

  return { totals, loading, error };
}
